import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetCurrentCounterpartyUserQuery } from '@/features/counterpartyUsers/counterpartyUsersApi';
import {
  selectedOrganizationIdSelector,
  setSelectedOrganizationId,
} from '@/features/ui/uiSlice';

/**
 * A user can be a different counterparty per organization, so pages
 * that read per-organization data (Dashboard, Contracts, ...) show one
 * organization at a time rather than aggregating across all of them. The
 * choice lives in redux (ui slice) so every component reading it — including
 * the picker in the global Header — re-renders when it changes, and is
 * mirrored into sessionStorage so it survives a reload. It is remembered per
 * browser tab only — it is not part of any account-wide/server-side state.
 */
const useSelectedOrganization = () => {
  const dispatch = useAppDispatch();
  const { data: counterpartyUser, isLoading } =
    useGetCurrentCounterpartyUserQuery();
  const organizations = counterpartyUser?.organizations ?? [];

  const organizationId = useAppSelector(selectedOrganizationIdSelector);

  useEffect(() => {
    if (isLoading || organizations.length === 0) return;

    const isValid = organizations.some(o => o.id === organizationId);
    if (!isValid) {
      dispatch(setSelectedOrganizationId(organizations[0].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, organizations]);

  const handleChange = (id: string) => {
    dispatch(setSelectedOrganizationId(id));
  };

  return {
    organizations,
    organizationId,
    onChange: handleChange,
    isLoading,
  };
};

export default useSelectedOrganization;
